import { Box, Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import { useState, useContext } from 'react';
import { ArrowBack, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { QButton, QContainer, QCard, QTypography, QList, QListItemText, QListItemButton } from '@kw/quokka-ui';
import { LaunchDarklyFlags } from '../../../launch-darkly';
import { RequestAnalysisInfoContext } from './RequestAnalysisInfoContext';

export default function RequestAnalysisLanding() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { setPlatform } = useContext(RequestAnalysisInfoContext);
  const flags: LaunchDarklyFlags['flags'] = useFlags();

  const handleSelectClick = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const navigate = useNavigate();

  const handleChoice = (choice: 'ios' | 'android') => {
    setPlatform(choice);
    navigate('/request-analysis/search');
    handleClose();
  };

  const handleBulkSubmitClick = () => {
    navigate('/request-analysis/bulk-submit');
  };

  const handleUploadPackageClick = () => {
    navigate('/request-analysis/upload-package');
  };

  return (
    <QContainer>
      <CardsGrid>
        <OptionCard>
          <IconRow>
            <img width={50} height={50} src={'/icons/google-play-store.png'} alt="google-play-store" />
            <img width={50} height={50} src={'/icons/ios-app-store.png'} alt="ios-app-store" />
          </IconRow>
          <QTypography component="h2" variant="h4Medium" align="center" fontWeight={800}>
            Search for Store Apps
          </QTypography>
          <QTypography variant="body2" align="center">
            Request analysis for an app by name, bundle ID, vendor, or store link.
          </QTypography>
          <SelectButton onClick={handleSelectClick} endIcon={<KeyboardArrowDownIcon />}>
            Select
          </SelectButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <PopoverList>
              {['Start with iOS/iPadOS', 'Start with Android'].map(label => {
                const value = label.includes('iOS') ? 'ios' : 'android';
                return (
                  <QListItemButton key={label} onClick={() => handleChoice(value as 'ios' | 'android')}>
                    <QListItemText primary={label} />
                  </QListItemButton>
                );
              })}
            </PopoverList>
          </Popover>
        </OptionCard>

        <OptionCard>
          <IconRow>
            <img width={50} height={50} src={'/icons/files.svg'} alt="bulk-submit" />
          </IconRow>
          <QTypography component="h2" variant="h4Medium" align="center" fontWeight={800}>
            Bulk Submit
          </QTypography>
          <QTypography variant="body2" align="center">
            Bulk submit multiple analysis requests at once via file upload or text entry.
          </QTypography>
          <SelectButton onClick={handleBulkSubmitClick}>Select</SelectButton>
        </OptionCard>

        {flags.applicationBinarySubmit && (
          <OptionCard>
            <IconRow>
              <FileUploadOutlinedIcon sx={{ fontSize: 50 }} />
            </IconRow>
            <QTypography component="h2" variant="h4Medium" align="center" fontWeight={800}>
              Upload a Package
            </QTypography>
            <QTypography variant="body2" align="center">
              Request analysis of an APK, AAB or IPA file via direct upload.
            </QTypography>
            <SelectButton onClick={handleUploadPackageClick}>Select</SelectButton>
          </OptionCard>
        )}
      </CardsGrid>
    </QContainer>
  );
}

const StyledQButton = styled(QButton)`
  margin-bottom: 10px;
`;

const CardsGrid = styled('div')({
  paddingTop: '27px',
  display: 'grid',
  gap: 24,
  gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
  alignItems: 'stretch',
});

const OptionCard = styled(QCard)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.greyOverride?.[400]}`,
  boxShadow: 'none',
  borderRadius: 8,
  height: '289px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  textAlign: 'center',
}));

const IconRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: 16,
});

const SelectButton = styled(QButton)({
  alignSelf: 'center',
  minWidth: 120,
  height: 48,
  background: '#487F87',
  '&:hover': { background: '#3a6b72' },
  color: '#fff',
  fontWeight: 700,
});

const PopoverList = styled(QList)`
  min-width: 170px;
  padding-top: 0;
  padding-bottom: 0;
`;
